import React from 'react';
import { FaUserCircle } from 'react-icons/fa';
import { palette, SECTION_ICONS } from './ui';
import { COMPLETENESS_WEIGHTS, SECTION_LABELS } from './constants';

function Ring({ percent, isDarkMode }) {
  const r = 34;
  const circumference = 2 * Math.PI * r;
  return (
    <svg width="88" height="88" viewBox="0 0 88 88" className="shrink-0">
      <defs>
        <linearGradient id="ring-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#7C3AED" />
          <stop offset="100%" stopColor="#F15A25" />
        </linearGradient>
      </defs>
      <circle cx="44" cy="44" r={r} fill="none" strokeWidth="7" className={isDarkMode ? 'stroke-gray-800' : 'stroke-gray-100'} />
      <circle
        cx="44" cy="44" r={r} fill="none" strokeWidth="7" strokeLinecap="round"
        stroke="url(#ring-grad)"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - percent / 100)}
        transform="rotate(-90 44 44)"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
      <text x="44" y="49" textAnchor="middle" className={`text-base font-bold ${isDarkMode ? 'fill-gray-100' : 'fill-ink-900'}`}>
        {percent}%
      </text>
    </svg>
  );
}

export default function CompletenessCard({ isDarkMode, profile, email, completeness, filledSections, onJumpTo }) {
  const c = palette(isDarkMode);
  const isBlueCollar = profile.work_category === 'blue_collar';
  const name = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Your profile';

  // best next nudge: highest-weight unfilled section
  const nudge = Object.entries(COMPLETENESS_WEIGHTS)
    .filter(([key]) => !(key === 'resume' && isBlueCollar))
    .filter(([key]) => !filledSections[key])
    .sort((a, b) => b[1] - a[1])[0];

  const sectionIds = {
    basic: 'basic', preferences: 'preferences', experience: 'experience',
    education: 'education', skills: 'skills', languages: 'languages', resume: 'resume',
  };

  const quickLinks = ['basic', 'preferences', 'experience', 'education', 'skills', 'languages', 'certifications', 'resume', 'identity'];

  return (
    <div className={`rounded-2xl border shadow-glass overflow-hidden ${c.card}`}>
      {/* branded header band */}
      <div className="h-20 bg-gradient-to-r from-brand-purple to-brand-saffron" />
      <div className="px-5 pb-5 -mt-10">
        <div className="flex flex-col items-center text-center">
          {profile.avatar_url ? (
            <img src={profile.avatar_url} alt="" className={`w-20 h-20 rounded-full object-cover border-4 ${isDarkMode ? 'border-[#161616]' : 'border-white'}`} />
          ) : (
            <div className={`w-20 h-20 rounded-full flex items-center justify-center border-4 ${isDarkMode ? 'border-[#161616] bg-gray-800' : 'border-white bg-ink-100'}`}>
              <FaUserCircle className={`w-16 h-16 ${c.secondaryText}`} />
            </div>
          )}
          <h1 className={`mt-3 text-lg font-bold font-display ${c.text}`}>{name}</h1>
          {profile.headline && <p className={`text-sm mt-0.5 ${c.secondaryText}`}>{profile.headline}</p>}
          {email && <p className={`text-xs mt-0.5 ${c.secondaryText}`}>{email}</p>}
        </div>

        <div className={`mt-4 flex items-center gap-3 rounded-xl p-3 ${isDarkMode ? 'bg-gray-800/50' : 'bg-ink-100/60'}`}>
          <Ring percent={completeness} isDarkMode={isDarkMode} />
          <div className="text-left min-w-0">
            <p className={`text-sm font-semibold ${c.text}`}>Profile strength</p>
            {nudge ? (
              <button
                onClick={() => onJumpTo(sectionIds[nudge[0]] || nudge[0])}
                className={`text-sm font-medium ${c.accent} hover:underline text-left mt-1`}
              >
                Add {SECTION_LABELS[nudge[0]].toLowerCase()} to reach {Math.min(100, completeness + Math.round(nudge[1]))}% →
              </button>
            ) : (
              <p className="text-sm font-medium text-brand-green mt-1">All done — great profile!</p>
            )}
          </div>
        </div>

        <nav className={`mt-5 pt-4 border-t ${c.divider}`}>
          <ul className="space-y-0.5">
            {quickLinks.map(id => {
              const meta = SECTION_ICONS[id];
              const done = filledSections[id];
              return (
                <li key={id}>
                  <button
                    onClick={() => onJumpTo(id)}
                    className={`w-full flex items-center gap-2.5 text-sm px-3 py-2 rounded-lg transition-colors ${c.text} ${c.hover}`}
                  >
                    {meta && <meta.Icon className="h-3.5 w-3.5" style={{ color: meta.color }} />}
                    <span className="flex-1 text-left">{SECTION_LABELS[id]}</span>
                    {done && <span className="w-1.5 h-1.5 rounded-full bg-brand-green" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </div>
  );
}
